import { Link } from "react-router-dom";
import { BrandLockup } from "@/components/BrandLogo";
import { Shield, Mail, Lock, Server, FileText, Users2 } from "lucide-react";

// Feb-21: Play Store compliant Privacy Policy — hosted at /privacy, publicly
// accessible over HTTPS, non-editable webpage, application-specific. Google Play
// Console requires this exact URL to be added under Dashboard → Set privacy policy.
export default function Privacy() {
  const updated = "September 17, 2026";
  return (
    <div className="min-h-screen bg-[#050914] text-slate-200">
      <header className="max-w-4xl mx-auto px-6 py-8 flex items-center justify-between">
        <Link to="/"><BrandLockup size="sm"/></Link>
        <Link to="/" data-testid="privacy-back-home" className="text-xs text-nb-cyan hover:text-white">← Back to Home</Link>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 pb-20">
        <div className="text-xs uppercase tracking-widest text-nb-cyan mb-2">Legal</div>
        <h1 className="font-heading font-black text-4xl md:text-5xl text-white tracking-tight">Privacy Policy</h1>
        <div className="mt-3 text-sm text-slate-400">Last updated: {updated} · Applies to the Next Billion mobile & web application, nextbillion.world.</div>

        <div className="mt-6 nb-card p-5 border-nb-cyan/30 flex items-start gap-3">
          <Shield size={22} className="text-nb-cyan shrink-0 mt-0.5"/>
          <div className="text-sm">
            <div className="text-white font-semibold">In plain English</div>
            <p className="mt-1 text-slate-300 leading-relaxed">Next Billion does <span className="text-white font-semibold">NOT sell</span>, rent, or share your personal information with advertisers or third parties for marketing. We only collect the minimum data required to run your account (email, wallet address, KYC documents where legally required) and keep it encrypted on our own servers. You can request full deletion at any time by writing to us.</p>
          </div>
        </div>

        <Section icon={FileText} title="1. Who we are">
          <p>Next Billion (&quot;we&quot;, &quot;us&quot;, &quot;the app&quot;) is an AI-powered crypto trading & referral network operated by the Next Billion team. The mobile application is a wrapper around our web platform hosted at <a className="text-nb-cyan" href="https://nextbillion.world">nextbillion.world</a>. This policy governs BOTH the Google Play mobile app and the web platform.</p>
        </Section>

        <Section icon={Lock} title="2. Data we collect">
          <p>We collect the minimum data required to operate your account and comply with local regulations. Specifically:</p>
          <ul className="list-disc list-inside space-y-1 mt-2">
            <li><b>Account data</b>: your email address, chosen password (stored hashed), display name, referral code.</li>
            <li><b>Financial data</b>: your BEP20 / TRC20 USDT wallet addresses, deposit / withdrawal amounts, transaction history.</li>
            <li><b>KYC data</b> (only when you voluntarily submit it): government-issued ID photo, selfie, address, phone number.</li>
            <li><b>Device / usage data</b>: your IP address at login, browser or device type, session tokens. Used for security only.</li>
            <li><b>App-specific data</b>: bot investments, staking amounts, team activity — necessary to compute your daily earnings.</li>
          </ul>
          <p className="mt-3"><b>We do NOT collect:</b> your contacts, SMS, photo library, precise location, microphone or camera data (KYC uses your camera only at the moment you tap &quot;Upload&quot; and we don&apos;t retain the raw stream).</p>
        </Section>

        <Section icon={Users2} title="3. How we use your data">
          <ul className="list-disc list-inside space-y-1">
            <li>To create and secure your account.</li>
            <li>To credit daily bot profits, referral bonuses and rank bonuses to your wallet.</li>
            <li>To process deposits & withdrawals on the blockchain.</li>
            <li>To send account-related notifications (deposit confirmation, withdrawal receipt, password reset).</li>
            <li>To detect and prevent fraud, unauthorised access and abuse.</li>
            <li>To meet legal and tax obligations where applicable.</li>
          </ul>
          <p className="mt-3"><b>We do NOT use your data for advertising, profiling, or resale.</b></p>
        </Section>

        <Section icon={Server} title="4. How we store & protect your data">
          <ul className="list-disc list-inside space-y-1">
            <li>All personal data is stored on private servers with restricted access.</li>
            <li>Passwords are hashed with industry-standard bcrypt — nobody, including our staff, can see your plaintext password.</li>
            <li>All traffic between the app and our servers is encrypted over HTTPS (TLS).</li>
            <li>KYC documents are encrypted at rest and accessible only to compliance staff.</li>
            <li>We retain your data as long as your account is active, plus a legally-required minimum (typically 5 years for financial records) after account deletion.</li>
          </ul>
        </Section>

        <Section icon={Users2} title="5. Data sharing — third parties we use">
          <p>We share the absolute minimum data with only these processors, each of whom acts on our behalf and cannot use your data for their own purposes:</p>
          <ul className="list-disc list-inside space-y-1 mt-2">
            <li><b>OKX Public Market API</b> — for crypto price feeds. No user data is sent.</li>
            <li><b>Resend</b> — for transactional email delivery (OTP, receipts, notifications). Receives only your email address.</li>
            <li><b>Emergent LLM (Anthropic / OpenAI / Gemini)</b> — powers optional AI assistant features. No PII is sent unless you type it into the chat.</li>
            <li><b>Google Play & PostHog analytics</b> — anonymous usage counters (install, crash reports, screen views). Does NOT include your identity.</li>
          </ul>
          <p className="mt-3"><b>We never sell your data. Ever.</b></p>
        </Section>

        <Section icon={Shield} title="6. Your rights">
          <ul className="list-disc list-inside space-y-1">
            <li><b>Access</b> — request a copy of the data we hold about you.</li>
            <li><b>Correction</b> — update any incorrect data via your Profile page.</li>
            <li><b>Deletion</b> — request permanent deletion of your account and all personal data (subject to the legal retention minimum for financial records).</li>
            <li><b>Portability</b> — request your data in a machine-readable format.</li>
            <li><b>Withdraw consent</b> — you can revoke consent for optional processing at any time.</li>
          </ul>
          <p className="mt-3">To exercise any of these rights, email us at the address below. We respond within 30 days.</p>
        </Section>

        <Section icon={Users2} title="7. Children">
          <p>Next Billion is <b>not directed at children under 18</b>. We do not knowingly collect data from anyone under 18. If you believe we have accidentally collected data from a minor, please contact us and we will delete it immediately.</p>
        </Section>

        <Section icon={FileText} title="8. Changes to this policy">
          <p>We may update this policy from time to time. The &quot;Last updated&quot; date at the top will change. Material changes will be announced in-app at least 14 days before they take effect. Continued use of the app after that date means you accept the new policy.</p>
        </Section>

        <Section icon={Mail} title="9. Contact us">
          <p>For any privacy question, data-access request, or complaint, contact us at:</p>
          <ul className="mt-2 space-y-1">
            <li><b>Email</b>: <a className="text-nb-cyan" href="mailto:support@nextbillion.world">support@nextbillion.world</a></li>
            <li><b>Website</b>: <a className="text-nb-cyan" href="https://nextbillion.world">https://nextbillion.world</a></li>
            <li><b>Telegram</b>: <a className="text-nb-cyan" href="https://t.me/nextbillionsupport">@nextbillionsupport</a></li>
          </ul>
          <p className="mt-3 text-slate-400 text-xs">If you are in the EU / UK, you also have the right to lodge a complaint with your national data-protection authority.</p>
        </Section>

        <div className="mt-10 border-t border-white/10 pt-6 text-xs text-slate-500 flex flex-col sm:flex-row justify-between gap-3">
          <div>© 2026 Next Billion. All rights reserved.</div>
          <div className="flex gap-4">
            <Link to="/terms" className="hover:text-white">Terms of Service</Link>
            <Link to="/privacy" className="hover:text-white">Privacy Policy</Link>
            <Link to="/" className="hover:text-white">Home</Link>
          </div>
        </div>
      </main>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <section className="mt-8">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={18} className="text-nb-cyan"/>
        <h2 className="font-heading font-bold text-xl text-white">{title}</h2>
      </div>
      <div className="text-sm text-slate-300 leading-relaxed">{children}</div>
    </section>
  );
}
